import { onMounted, onUnmounted, ref } from 'vue'
import { useAuthStore } from '@/modules/auth/stores/auth.store'
import router from '@/router'

const STORAGE_KEY = 'session_last_activity'
const WARNING_BEFORE_TIMEOUT_MS = 60 * 1000 // Show warning 1 minute before timeout

export function useSessionTimeout(timeoutMinutes = 30) {
  const auth = useAuthStore()
  let timeoutId: number | null = null
  let warningTimeoutId: number | null = null

  const showWarning = ref(false)
  const remainingSeconds = ref(0)
  let countdownInterval: number | null = null

  const timeoutMs = timeoutMinutes * 60 * 1000

  const updateLastActivity = () => {
    const now = Date.now()
    localStorage.setItem(STORAGE_KEY, now.toString())
  }

  const getLastActivity = (): number => {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? parseInt(stored, 10) : Date.now()
  }

  const clearAllTimers = () => {
    if (timeoutId) clearTimeout(timeoutId)
    if (warningTimeoutId) clearTimeout(warningTimeoutId)
    if (countdownInterval) clearInterval(countdownInterval)
    timeoutId = null
    warningTimeoutId = null
    countdownInterval = null
  }

  const performLogout = () => {
    clearAllTimers()
    showWarning.value = false
    localStorage.removeItem(STORAGE_KEY)
    auth.logout()
    router.push('/auth/login')
  }

  const startWarningCountdown = () => {
    showWarning.value = true
    remainingSeconds.value = Math.floor(WARNING_BEFORE_TIMEOUT_MS / 1000)

    countdownInterval = window.setInterval(() => {
      remainingSeconds.value--
      if (remainingSeconds.value <= 0) {
        if (countdownInterval) clearInterval(countdownInterval)
      }
    }, 1000)
  }

  const resetTimer = () => {
    // Only reset if user is logged in
    if (!auth.isLoggedIn) return

    clearAllTimers()
    showWarning.value = false
    updateLastActivity()

    // Set warning timer (fires 1 minute before logout)
    const warningDelay = timeoutMs - WARNING_BEFORE_TIMEOUT_MS
    if (warningDelay > 0) {
      warningTimeoutId = window.setTimeout(() => {
        startWarningCountdown()
      }, warningDelay)
    }

    // Set logout timer
    timeoutId = window.setTimeout(() => {
      performLogout()
    }, timeoutMs)
  }

  const extendSession = () => {
    resetTimer()
  }

  // Handle cross-tab synchronization
  const handleStorageChange = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY && event.newValue) {
      // Another tab updated activity, sync our timers
      const lastActivity = parseInt(event.newValue, 10)
      const elapsed = Date.now() - lastActivity

      if (elapsed < timeoutMs) {
        // Reset our timers based on the new activity
        clearAllTimers()
        showWarning.value = false

        const remainingTime = timeoutMs - elapsed
        const warningDelay = remainingTime - WARNING_BEFORE_TIMEOUT_MS

        if (warningDelay > 0) {
          warningTimeoutId = window.setTimeout(() => {
            startWarningCountdown()
          }, warningDelay)
        } else if (remainingTime > 0) {
          startWarningCountdown()
        }

        timeoutId = window.setTimeout(() => {
          performLogout()
        }, remainingTime)
      }
    }

    // If session storage was cleared (logout in another tab)
    if (event.key === STORAGE_KEY && event.newValue === null) {
      clearAllTimers()
      showWarning.value = false
      if (auth.isLoggedIn) {
        auth.logout()
        router.push({ name: 'auth.login' })
      }
    }
  }

  const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove']

  onMounted(() => {
    // Check if session already expired on mount
    const lastActivity = getLastActivity()
    const elapsed = Date.now() - lastActivity

    if (auth.isLoggedIn && elapsed >= timeoutMs) {
      performLogout()
      return
    }

    events.forEach(event => window.addEventListener(event, resetTimer))
    window.addEventListener('storage', handleStorageChange)
    resetTimer()
  })

  onUnmounted(() => {
    events.forEach(event => window.removeEventListener(event, resetTimer))
    window.removeEventListener('storage', handleStorageChange)
    clearAllTimers()
  })

  return {
    showWarning,
    remainingSeconds,
    extendSession,
  }
}
